import useAgentInterception from '../../../hooks/agentInterception/useAgentInterception.hooks';
import { selectIsAgentActionBlocked } from '../../../redux/features/agentMode/agentMode.selector';
import logger from '../../../utils/logger';
import { AgentInterceptionType } from '../../../reducers/messagingSlice';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { sendSpokenPrompt } from '../../../reducers/spokenPromptThunk';
import { TopLevelMenuItem } from '../../../utils/menu';
import { shouldSkipDonationPrompt } from '../../../utils/TTSMessage';
import { selectOrderValues } from '../../../selectors/order';
import { SLASH_COMMAND_REMINDERS } from '../../../utils/constants';
import {
  selectPromptAskCounts,
  selectPromptOverrides,
} from '../../../redux/features/experiments/experiments.selector';
import {
  getSpokenPrompt,
  slashCommandPromptSlot,
  toPromptSlotKey,
} from '../../../redux/features/experiments/experiments.utils';
import { promptAsked } from '../../../redux/features/experiments/experiments.slice';
import { IPromptSlot } from '../../../redux/features/experiments/experiments.props';

const useAutocompleteMessage = () => {
  const dispatch = useAppDispatch();
  const [currentMessage, setCurrentMessage] = useState('');
  const { sendAgentInterception } = useAgentInterception();
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);
  const { total } = useSelector(selectOrderValues);
  const promptOverrides = useAppSelector(selectPromptOverrides);
  const promptAskCounts = useAppSelector(selectPromptAskCounts);
  const updateCurrentMessage = (message: string) => {
    setCurrentMessage(message);
  };
  const sendAgentInterventionEvent = (
    type: AgentInterceptionType,
    description?: string
  ) => {
    sendAgentInterception({
      data: {
        type,
        description,
      },
    });
  };
  /**
   * HITL-1587: placeholder substitution, the no-total skip, and the
   * spoken-total bookkeeping all live in the shared sendSpokenPrompt thunk —
   * the same path the cart-add auto-prompt and the center-pane heading use.
   * Only the interception (whose side effects are surface-specific) is
   * stamped here, and only when something was actually spoken. Returns
   * whether the prompt went out.
   */
  const sendMessage = (
    message: string,
    metadata?: any,
    title?: string
  ): boolean => {
    // HITL-1606: outside Intercept the agent may not speak or send. Guarded
    // HERE, at the send, not only in useAgentInterception: these paths dispatch
    // the outbound frame FIRST and stamp the interception after, so refusing the
    // interception alone would still let the customer hear it. Reachable by
    // keyboard with no button to disable, so a refusal here is expected, not a
    // defect — debug, not warn.
    if (isAgentActionBlocked) {
      logger.debug({
        message: 'Omnibar send refused: the agent is not in Intercept mode',
      });
      return false;
    }
    const { spoken } = dispatch(sendSpokenPrompt({ message, metadata }));
    if (spoken) {
      sendAgentInterventionEvent(AgentInterceptionType.TTS_SENT);
    }
    return spoken;
  };

  /**
   * HITL-1570: send the donation upsell prompt, gated on the constraint's
   * `speak_total_in_upsell` condition — with the condition set and no check
   * total yet, the donation upsell is skipped entirely (nothing is sent, and
   * false is returned so the caller skips its side effects too). The `{total}`
   * substitution and the spoken-total bookkeeping live in the shared
   * sendSpokenPrompt thunk that sendMessage routes through (HITL-1587); its
   * own no-total gate also covers a `{total}`-templated prompt whose
   * constraint is missing the condition. Returns whether the prompt was sent.
   */
  const sendDonationPromptMessage = (
    command: string,
    speakTotalInUpsell: boolean | undefined,
    metadata?: any
  ): boolean => {
    if (shouldSkipDonationPrompt({ speakTotalInUpsell, total })) {
      return false;
    }
    return sendMessage(command, metadata);
  };

  /**
   * HITL-1570: should a direct selection of this item be skipped outright?
   * True only for a donation item whose constraint declares
   * speak_total_in_upsell while no check total exists — the same gate the
   * reminder queue applies. Callers must then neither speak nor add the item.
   */
  const isDonationPromptSkipped = (item: {
    name?: string;
    speakTotalInUpsell?: boolean;
  }): boolean => {
    if ((item.name ?? '').split(':')[0] !== SLASH_COMMAND_REMINDERS.DONATION) {
      return false;
    }
    return shouldSkipDonationPrompt({
      speakTotalInUpsell: item.speakTotalInUpsell,
      total,
    });
  };

  /**
   * Returns whether a prompt was actually sent — false only when a gated
   * donation was skipped for lack of a check total, so the caller must skip
   * its own side effects (like adding the item to the cart) too.
   */
  const sendSlashCommandMessage = (
    item: TopLevelMenuItem,
    isUnifiedMenuEnabled: boolean
  ): boolean => {
    const itemName = item.name;
    let intent = '';
    let sendVal = '';
    let slot: IPromptSlot | undefined;
    if (isUnifiedMenuEnabled) {
      intent = itemName.replace('/', '');
      const { slashCommands } = item;
      if (slashCommands?.length) {
        slot = slashCommandPromptSlot(item);
        sendVal = getSpokenPrompt(
          promptOverrides,
          item.activeConstraintName,
          slashCommands,
          promptAskCounts[toPromptSlotKey(slot)]
        );
      }
    } else {
      [intent, sendVal] = itemName.replace('/', '').split(':');
    }
    // HITL-1570: the donation prompt is gated on the check total when its
    // constraint declares speak_total_in_upsell, and tracks whether the
    // total was spoken.
    const spoken =
      itemName.split(':')[0] === SLASH_COMMAND_REMINDERS.DONATION
        ? sendDonationPromptMessage(sendVal, item.speakTotalInUpsell, {
            intent,
          })
        : sendMessage(sendVal, { intent });
    if (spoken && slot) dispatch(promptAsked(slot));
    return spoken;
  };

  return {
    currentMessage,
    updateCurrentMessage,
    sendMessage,
    sendDonationPromptMessage,
    isDonationPromptSkipped,
    sendAgentInterventionEvent,
    sendSlashCommandMessage,
  };
};

export default useAutocompleteMessage;
